/**
 * @fileoverview Servizio per lettura e aggiornamento profili utente (collection users).
 */
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { COLLECTIONS, USERS_FIELDS } from '../domain/dataModel';

/**
 * Recupera un utente per UID.
 * @param {string} uid - Firebase Auth UID
 * @returns {Promise<{ id: string, ...Object } | null>} Documento utente con id o null
 */
export async function getUserById(uid) {
  try {
    if (!uid) return null;
    const ref = doc(db, COLLECTIONS.USERS, uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento dell\'utente.');
  }
}

/**
 * Aggiorna il profilo utente (es. displayName).
 * @param {string} uid - Firebase Auth UID
 * @param {Object} data - Campi da aggiornare (es. { displayName: '...' })
 * @returns {Promise<void>}
 */
export async function updateUserProfile(uid, data) {
  try {
    if (!uid) throw new Error('UID utente mancante.');
    const ref = doc(db, COLLECTIONS.USERS, uid);
    await updateDoc(ref, {
      ...data,
      [USERS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });
  } catch (err) {
    throw new Error(err?.message || 'Errore nell\'aggiornamento del profilo.');
  }
}

/**
 * Carica più utenti in parallelo per UID. Filtra i null dall'array uids.
 * @param {string[]} uids - Array di UID (i null vengono ignorati)
 * @returns {Promise<Record<string, Object>>} Mappa uid -> { id, ...data }
 */
export async function getUsersByIds(uids) {
  try {
    const validIds = (uids || []).filter((id) => id != null && id !== '');
    if (validIds.length === 0) return {};
    const snaps = await Promise.all(
      validIds.map((id) => getDoc(doc(db, COLLECTIONS.USERS, id)))
    );
    const result = {};
    validIds.forEach((id, i) => {
      const snap = snaps[i];
      if (snap?.exists()) result[id] = { id: snap.id, ...snap.data() };
    });
    return result;
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento degli utenti.');
  }
}
